import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { ROUTES } from '@shared/constants';
import { LOCAL_STORAGE_ITEM, getFromLocalStorage, setToLocalStorage } from '@utils/localStorageHelper';

const ONE_DAY = 24 * 60 * 60 * 1000;

const protectedCheck = (roles, user, token) => {
  if (!user || !token || !user.roles) {
    return false;
  }
  if (!roles.length) {
    return true;
  }
  const rolesObj = {};
  for (const role of roles) {
    rolesObj[role] = role;
  }
  for (const role of user.roles) {
    if (rolesObj[role]) {
      return true;
    }
  }
  return false;
};

const refreshCheck = () => {
  const refreshDate = getFromLocalStorage(LOCAL_STORAGE_ITEM.REFRESH_TIME);
  if (!refreshDate) {
    setToLocalStorage(LOCAL_STORAGE_ITEM.REFRESH_TIME, new Date().getTime());
  } else {
    const oneDay = refreshDate + ONE_DAY;
    const now = new Date().getTime();
    if (now >= oneDay) {
      setToLocalStorage(LOCAL_STORAGE_ITEM.REFRESH_TIME, new Date().getTime());
      window.location.reload(true);
    }
  }
};

export const ProtectedRoute = ({
  component: Component,
  roles,
  user,
  token,
  experimentalOnly = false,
  noRolesOnly = false,
  ...rest
}) => (
  <Route
    {...rest}
    render={(props) => {
      refreshCheck();
      const canAccessExperimentalRoute = !experimentalOnly || user?.experimentalFeatures === true;
      const canAccessNoRolesRoute = !noRolesOnly || user?.roles?.length === 0;
      if (protectedCheck(roles, user, token) && canAccessExperimentalRoute && canAccessNoRolesRoute) {
         return <Component {...props} />;
      }

      // Avoid redirect loops: if token exists but we don't yet have a valid user (or just logged out),
      // treat it as unauthenticated and send to sign-in.
      const hasUserRoles = Boolean(user && user.roles);
      return (
        <Redirect
          to={{
            pathname: token && hasUserRoles ? ROUTES.HOME : ROUTES.SIGN_IN,
            state: { from: props.location },
          }}
        />
      );
    }}
  />
);

ProtectedRoute.propTypes = {
  location: PropTypes.object,
  component: PropTypes.func,
  user: PropTypes.object,
  token: PropTypes.string,
  experimentalOnly: PropTypes.bool,
  noRolesOnly: PropTypes.bool,
};

export const mapStateToProps = (state) => {
  const user = state.getIn(['app', 'authentication', 'user']).toJS();
  const token = state.getIn(['app', 'authentication', 'token']) || null;
  return { user, token };
};

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(ProtectedRoute);
